import { c as _c } from "react/compiler-runtime";
import { DeleteMenuItem } from "@components/project/delete-menu-item";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { DeleteIcon, HolobuilderIcon, SelectionIcon, ShareLinkIcon, ViewerIcon } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { getOpenSnapshotHref, trackOpenSnapshot } from "@pages/project-details/project-snapshots/snapshot-utils";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { getOpenInTargetLabel } from "@utils/project-utils";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { toggleItemSelection } from "@utils/array-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SnapshotShareMenuItem } from "@pages/project-details/project-snapshots/snapshot-share-menu-item";

/**
 * Custom hook to get the menu options for a snapshot.
 * Provides actions for viewing details, opening in different targets, sharing, and deleting a snapshot.
 */
export function useSnapshotMenuOptions() {
  const $ = _c(37);
  const {
    trackAsyncEvent
  } = useTrackEvent();
  const dispatch = useAppDispatch();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const snapshotLaunchSettings = useAppSelector(defaultProjectLaunchTargetSelector);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  let t0;
  if ($[0] !== trackAsyncEvent) {
    t0 = function openSnapshot(snapshot, openTarget) {
      trackOpenSnapshot({
        openTarget,
        trackAsyncEvent,
        openFrom: "snapshots-menu-options",
        accessLevel: snapshot.accessLevel,
        createdAt: snapshot.createdAt
      });
      window.open(getOpenSnapshotHref({
        snapshotId: snapshot.id,
        openTarget
      }), "_self");
    };
    $[0] = trackAsyncEvent;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const openSnapshot = t0;
  let t1;
  if ($[2] !== dispatch || $[3] !== selectedRowIds) {
    t1 = snapshot_0 => {
      const newSelectedRowIds = toggleItemSelection(selectedRowIds, snapshot_0.id);
      dispatch(setInitialFetchingItems(newSelectedRowIds));
    };
    $[2] = dispatch;
    $[3] = selectedRowIds;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== isScreenSmOrSmaller) {
    t2 = snapshot_1 => isScreenSmOrSmaller && isUserSnapshotAdmin(snapshot_1);
    $[5] = isScreenSmOrSmaller;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      label: "Select snapshot",
      Icon: SelectionIcon,
      onSelect: t1,
      shouldShow: t2,
      shouldShowDividerAfter: true
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== isScreenSmOrSmaller || $[11] !== snapshotLaunchSettings.canOpenWebEditor) {
    t4 = getOpenInTargetLabel({
      target: ProjectLaunchTarget.sphereViewer,
      canOpenWebEditor: snapshotLaunchSettings.canOpenWebEditor,
      shouldOmitVerb: isScreenSmOrSmaller
    });
    $[10] = isScreenSmOrSmaller;
    $[11] = snapshotLaunchSettings.canOpenWebEditor;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== openSnapshot) {
    t5 = snapshot_2 => {
      openSnapshot(snapshot_2, ProjectLaunchTarget.sphereViewer);
    };
    $[13] = openSnapshot;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = {
      label: t4,
      Icon: ViewerIcon,
      onSelect: t5,
      getHrefUrl: _temp,
      shouldHandleNavigation: true
    };
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== isScreenSmOrSmaller || $[19] !== snapshotLaunchSettings.canOpenWebEditor) {
    t7 = getOpenInTargetLabel({
      target: ProjectLaunchTarget.webEditor,
      canOpenWebEditor: snapshotLaunchSettings.canOpenWebEditor,
      shouldOmitVerb: isScreenSmOrSmaller
    });
    $[18] = isScreenSmOrSmaller;
    $[19] = snapshotLaunchSettings.canOpenWebEditor;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== openSnapshot) {
    t8 = snapshot_4 => {
      openSnapshot(snapshot_4, ProjectLaunchTarget.webEditor);
    };
    $[21] = openSnapshot;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== snapshotLaunchSettings.canOpenWebEditor) {
    t9 = () => snapshotLaunchSettings.canOpenWebEditor;
    $[23] = snapshotLaunchSettings.canOpenWebEditor;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = {
      label: t7,
      Icon: HolobuilderIcon,
      onSelect: t8,
      getHrefUrl: _temp2,
      shouldHandleNavigation: true,
      shouldShow: t9,
      shouldShowDividerAfter: true
    };
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== isScreenSmOrSmaller) {
    t11 = {
      label: "Share",
      Icon: ShareLinkIcon,
      component: _temp3,
      shouldShow: () => isScreenSmOrSmaller
    };
    $[29] = isScreenSmOrSmaller;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      label: "Delete",
      Icon: DeleteIcon,
      component: _temp4,
      shouldShow: isUserSnapshotAdmin
    };
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t3 || $[35] !== t6) {
    t13 = [t3, t6, t10, t11, t12];
    $[32] = t10;
    $[33] = t11;
    $[34] = t3;
    $[35] = t6;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const menuOptions = t13;
  return menuOptions;
}
function _temp4(snapshot_7) {
  return <DeleteMenuItem type="snapshot" entity={snapshot_7} />;
}
function _temp3(snapshot_6) {
  return <SnapshotShareMenuItem snapshot={snapshot_6} />;
}
function _temp2(snapshot_5) {
  return getOpenSnapshotHref({
    snapshotId: snapshot_5.id,
    openTarget: ProjectLaunchTarget.webEditor
  });
}
function _temp(snapshot_3) {
  return getOpenSnapshotHref({
    snapshotId: snapshot_3.id,
    openTarget: ProjectLaunchTarget.sphereViewer
  });
}